import { useEffect, useRef, useState } from "react";
import { Button, Flex, Popconfirm, Progress, Tooltip, Typography } from "antd";
import { Ban, Clock, LogIn, Pause, Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { ExamPeriodMonitorState } from "@types";
import type { CandidateStateAction, ProctoringProgress } from "@hooks";
import { useClockOffset } from "../../hooks/useClockOffset";
import { anchorMonotonic, remainingSecondsMonotonic, type MonotonicAnchor } from "../exam-period/monitor/examTimer";

export const isInExamState = (state?: number) =>
  state === ExamPeriodMonitorState.InProgress || state === ExamPeriodMonitorState.Paused;

export const formatRemaining = (seconds: number) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const mm = String(m).padStart(h > 0 ? 2 : 1, "0");
  return `${h > 0 ? `${h}:` : ""}${mm}:${String(s).padStart(2, "0")}`;
};

/// Red in the last minute, orange in the last five; paused time is shown orange as well.
export const remainingColor = (seconds: number | null, paused: boolean) =>
  seconds === null ? "#bfbfbf" : seconds <= 60 ? "#ff4d4f" : seconds <= 300 || paused ? "#fa8c16" : "#ffffff";

/// Counts down from the server snapshot with the same monotonic clock as the player and the monitoring page.
export const useRemainingSeconds = (progress?: ProctoringProgress) => {
  const clockOffsetMs = useClockOffset();
  const anchorRef = useRef<MonotonicAnchor | null>(null);
  const [seconds, setSeconds] = useState<number | null>(null);
  const sync = progress && isInExamState(progress.state) ? progress.sync : null;

  useEffect(() => {
    const anchor = sync ? anchorMonotonic(sync, clockOffsetMs) : null;
    anchorRef.current = anchor;
    setSeconds(anchor ? remainingSecondsMonotonic(anchor) : null);
  }, [sync?.serverNowUnixMs, sync?.isRunning, sync, clockOffsetMs]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const anchor = anchorRef.current;
      if (anchor) setSeconds(remainingSecondsMonotonic(anchor));
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  return seconds;
};

const chip = (color: string, background = "rgba(0,0,0,0.55)") => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "1px 6px",
  borderRadius: 10,
  fontSize: 12,
  lineHeight: "18px",
  fontVariantNumeric: "tabular-nums" as const,
  color,
  background,
  whiteSpace: "nowrap" as const,
  flex: "none" as const,
});

/// Remaining time and progress chips for a wall tile (only while the exam runs or is paused).
export const TileExamBadges = ({ progress, compact }: { progress?: ProctoringProgress; compact: boolean }) => {
  const { t } = useTranslation();
  const seconds = useRemainingSeconds(progress);
  if (!progress || !isInExamState(progress.state)) return null;
  const paused = progress.state === ExamPeriodMonitorState.Paused;
  const color = remainingColor(seconds, paused);
  return (
    <Tooltip
      title={
        <div>
          <div>{t("proctoring.answered", { answered: progress.answered, total: progress.total })}</div>
          {seconds !== null && <div>{t("proctoring.timeLeft")}: {formatRemaining(seconds)}</div>}
          {progress.startedAt && <div>{t("proctoring.startedAt", { time: dayjs(progress.startedAt).format("HH:mm") })}</div>}
        </div>
      }
    >
      <span style={{ display: "inline-flex", gap: 4 }} data-testid="tile-exam-badges">
        {seconds !== null && (
          <span style={chip(color)} data-testid="tile-time-left">
            {paused ? <Pause size={11} /> : <Clock size={11} />}
            {formatRemaining(seconds)}
          </span>
        )}
        {!compact && <span style={chip("#ffffff")}>{progress.percentage}%</span>}
      </span>
    </Tooltip>
  );
};

/// Thin progress line along the bottom edge of a tile.
export const TileProgressLine = ({ progress }: { progress?: ProctoringProgress }) => {
  if (!progress || !(isInExamState(progress.state) || progress.state === ExamPeriodMonitorState.Completed)) return null;
  return (
    <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 3, background: "rgba(255,255,255,0.15)" }}>
      <div style={{ width: `${Math.min(100, progress.percentage)}%`, height: "100%", background: "#52c41a", transition: "width 0.6s" }} />
    </div>
  );
};

interface PanelProps {
  progress?: ProctoringProgress;
  state: number;
  candidateName: string;
  pendingAction?: CandidateStateAction | "admit" | null;
  onAdmit: () => void;
  onAction: (action: CandidateStateAction) => void;
  getPopupContainer?: () => HTMLElement;
}

/// "Exam" section of the focused candidate: progress ring, remaining time, facts and monitoring actions.
export const FocusExamPanel = ({ progress, state, candidateName, pendingAction, onAdmit, onAction, getPopupContainer }: PanelProps) => {
  const { t } = useTranslation();
  const seconds = useRemainingSeconds(progress);
  const paused = state === ExamPeriodMonitorState.Paused;
  const inExam = isInExamState(state);
  const hasStats = !!progress && (inExam || state === ExamPeriodMonitorState.Completed);

  return (
    <Flex gap={14} align="center" wrap="wrap" data-testid="focus-exam-panel">
      {hasStats && (
        <Tooltip title={t("proctoring.answered", { answered: progress!.answered, total: progress!.total })}>
          <Progress
            type="circle"
            size={58}
            percent={progress!.percentage}
            strokeColor="#52c41a"
            format={() => (
              <span style={{ fontSize: 12, color: "#fff" }}>
                {progress!.answered}/{progress!.total}
              </span>
            )}
          />
        </Tooltip>
      )}
      {inExam && (
        <Flex vertical gap={0}>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {paused ? t("pages.examPeriods.paused") : t("proctoring.timeLeft")}
          </Typography.Text>
          <span
            style={{ fontSize: 24, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: remainingColor(seconds, paused), lineHeight: 1.1 }}
            data-testid="focus-time-left"
          >
            {seconds === null ? "–" : formatRemaining(seconds)}
          </span>
        </Flex>
      )}
      {hasStats && (
        <Flex vertical gap={0} style={{ fontSize: 12 }}>
          {progress!.startedAt && (
            <Typography.Text type="secondary">{t("proctoring.startedAt", { time: dayjs(progress!.startedAt).format("HH:mm") })}</Typography.Text>
          )}
          <Typography.Text type={progress!.browserExits > 0 ? "warning" : "secondary"}>
            {t("proctoring.browserExits", { count: progress!.browserExits })}
          </Typography.Text>
          <Typography.Text type={progress!.tabSwitches > 0 ? "warning" : "secondary"}>
            {t("proctoring.tabSwitches", { count: progress!.tabSwitches })}
          </Typography.Text>
        </Flex>
      )}
      <Flex gap={8} wrap="wrap">
        {state === ExamPeriodMonitorState.WaitingForParticipant && (
          <Button type="primary" icon={<LogIn size={16} />} loading={pendingAction === "admit"} onClick={onAdmit} data-testid="focus-admit">
            {t("proctoring.admit")}
          </Button>
        )}
        {state === ExamPeriodMonitorState.InProgress && (
          <Popconfirm
            title={t("proctoring.pauseConfirm", { name: candidateName })}
            onConfirm={() => onAction("pause")}
            getPopupContainer={getPopupContainer}
          >
            <Button icon={<Pause size={16} />} loading={pendingAction === "pause"} data-testid="focus-pause">
              {t("proctoring.pause")}
            </Button>
          </Popconfirm>
        )}
        {paused && (
          <Button type="primary" icon={<Play size={16} />} loading={pendingAction === "resume"} onClick={() => onAction("resume")} data-testid="focus-resume">
            {t("proctoring.resume")}
          </Button>
        )}
        {inExam && (
          <Popconfirm
            title={t("proctoring.excludeConfirm", { name: candidateName })}
            okButtonProps={{ danger: true }}
            onConfirm={() => onAction("exclude")}
            getPopupContainer={getPopupContainer}
          >
            <Button danger icon={<Ban size={16} />} loading={pendingAction === "exclude"} data-testid="focus-exclude">
              {t("proctoring.exclude")}
            </Button>
          </Popconfirm>
        )}
      </Flex>
    </Flex>
  );
};
